import React, { useState, useEffect } from 'react';
import MapboxMap, { Marker, Popup, Source, Layer } from 'react-map-gl/mapbox';
import { DEFAULT_MAP_CONFIG, handleMapboxError, useMapboxToken } from '../utils/mapbox';

interface MapboxMapWrapperProps {
  center: [number, number]; // [latitude, longitude]
  zoom?: number;
  markers?: Array<{
    position: [number, number]; // [latitude, longitude]
    content: React.ReactNode;
    popup?: React.ReactNode;
  }>;
  route?: {
    coordinates: [number, number][]; // Array of [latitude, longitude] pairs
    color?: string;
    width?: number;
  };
  height?: string;
  scrollZoom?: boolean;
  onLoad?: () => void;
  onError?: (error: any) => void;
  className?: string;
  style?: React.CSSProperties;
}

const MapboxMapWrapper: React.FC<MapboxMapWrapperProps> = ({
  center,
  zoom = DEFAULT_MAP_CONFIG.zoom,
  markers = [],
  route,
  height = '100%',
  scrollZoom = true,
  onLoad,
  onError,
  className = '',
  style = {},
}) => {
  const { token: mapboxToken, loading: tokenLoading, valid: tokenValid } = useMapboxToken();
  const [mapError, setMapError] = useState<string | null>(null);
  const [openPopups, setOpenPopups] = useState<Set<number>>(new Set());
  const [viewState, setViewState] = useState({
    longitude: center[1], // Mapbox uses [lng, lat]
    latitude: center[0],
    zoom: zoom,
  });

  // Update viewState when center or zoom changes
  useEffect(() => {
    setViewState({
      longitude: center[1],
      latitude: center[0],
      zoom: zoom,
    });
  }, [center[0], center[1], zoom]);

  const handleError = (e: any) => {
    handleMapboxError(e, setMapError);
    if (onError) {
      onError(e);
    }
  };

  const handleMapLoad = () => {
    setMapError(null);
    if (onLoad) {
      onLoad();
    }
  };

  const togglePopup = (index: number) => {
    setOpenPopups(prev => {
      const newSet = new Set(prev);
      if (newSet.has(index)) {
        newSet.delete(index);
      } else {
        newSet.add(index);
      }
      return newSet;
    });
  };

  if (tokenLoading) {
    return (
      <div
        className={`w-full bg-gray-50 border border-gray-200 rounded-lg flex items-center justify-center ${className}`}
        style={{ height, ...style }}
      >
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-green-600 mb-2" />
          <p className="text-sm text-gray-600">Loading map...</p>
        </div>
      </div>
    );
  }

  if (!tokenValid || !mapboxToken) {
    return (
      <div className={`w-full bg-yellow-50 border border-yellow-300 rounded-lg flex items-center justify-center ${className}`} style={{ height, ...style }}>
        <div className="text-center p-6 max-w-md">
          <p className="text-yellow-800 font-semibold text-lg mb-2">Mapbox token required</p>
          <p className="text-yellow-700 text-sm mb-4">
            To display maps, set a Mapbox public token on the server or in the client build.
          </p>
          <div className="bg-white rounded-lg p-4 text-left text-sm text-gray-700">
            <p className="font-semibold mb-2">Steps to fix:</p>
            <ol className="list-decimal list-inside space-y-1">
              <li>Go to <a href="https://account.mapbox.com/" target="_blank" rel="noopener noreferrer" className="text-green-600 hover:text-green-700 underline">mapbox.com</a> and create a public token</li>
              <li>Add to <code className="bg-gray-100 px-1 rounded">server/.env</code>: <code className="bg-gray-100 px-1 rounded">MAPBOX_ACCESS_TOKEN=pk...</code></li>
              <li>Or add to <code className="bg-gray-100 px-1 rounded">client/.env</code>: <code className="bg-gray-100 px-1 rounded">REACT_APP_MAPBOX_TOKEN=pk...</code></li>
              <li>Restart the dev server</li>
            </ol>
          </div>
        </div>
      </div>
    );
  }

  // Create route GeoJSON if route is provided
  const routeGeoJson = route ? {
    type: 'Feature' as const,
    geometry: {
      type: 'LineString' as const,
      coordinates: route.coordinates.map(([lat, lng]) => [lng, lat]), // Convert to [lng, lat] for GeoJSON
    },
    properties: {},
  } : null;

  return (
    <div className={`w-full relative ${className}`} style={{ height, ...style }}>
      {mapError && (
        <div className="absolute inset-0 bg-gray-100 bg-opacity-90 flex items-center justify-center z-50 rounded-lg">
          <div className="text-center p-4">
            <p className="text-red-600 font-semibold mb-2">Map failed to load</p>
            <p className="text-sm text-gray-600">{mapError}</p>
          </div>
        </div>
      )}
      <MapboxMap
        mapboxAccessToken={mapboxToken}
        {...viewState}
        onMove={evt => setViewState(evt.viewState)}
        style={{ width: '100%', height: '100%' }}
        mapStyle={DEFAULT_MAP_CONFIG.style}
        scrollZoom={scrollZoom}
        onError={handleError}
        onLoad={handleMapLoad}
      >
        {/* Route line */}
        {route && routeGeoJson && (
          <Source id="route" type="geojson" data={routeGeoJson}>
            <Layer
              id="route-line"
              type="line"
              layout={{
                'line-join': 'round',
                'line-cap': 'round',
              }}
              paint={{
                'line-color': route.color || '#22C55E',
                'line-width': route.width || 4,
                'line-opacity': 0.6,
                'line-dasharray': [2, 2],
              }}
            />
          </Source>
        )}

        {/* Markers */}
        {markers.map((marker, index) => (
          <React.Fragment key={index}>
            <Marker
              longitude={marker.position[1]} // Mapbox uses [lng, lat]
              latitude={marker.position[0]}
              anchor="bottom"
            >
              <div
                className="cursor-pointer"
                onClick={() => marker.popup && togglePopup(index)}
              >
                {marker.content}
              </div>
            </Marker>
            {marker.popup && openPopups.has(index) && (
              <Popup
                longitude={marker.position[1]}
                latitude={marker.position[0]}
                anchor="bottom"
                onClose={() => togglePopup(index)}
                closeButton={true}
                closeOnClick={false}
              >
                {marker.popup}
              </Popup>
            )}
          </React.Fragment>
        ))}
      </MapboxMap>
    </div>
  );
};

export default MapboxMapWrapper;

